'use client'

import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'

import { cn } from '@/utils/cn'
import { CheckIcon, MinusIcon } from 'lucide-react'

/**
 * Checkbox Component
 *
 * A control that allows the user to toggle between checked and not checked.
 *
 * @see https://ui.shadcn.com/docs/components/base/checkbox
 *
 * @example
 * ```tsx
 * <Field orientation="horizontal">
 *   <Checkbox id="terms" />
 *   <FieldLabel htmlFor="terms">Accept terms and conditions</FieldLabel>
 * </Field>
 * ```
 *
 * @remarks
 * Key Props:
 * - `defaultChecked`: uncontrolled initial state
 * - `checked`, `onCheckedChange`: controlled state
 * - `disabled`: disables interaction
 */

function Checkbox({ className, ...props }: CheckboxPrimitive.Root.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'dark:bg-input/30 data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary data-checked:border-primary data-indeterminate:bg-primary data-indeterminate:text-primary-foreground data-indeterminate:border-primary aria-invalid:aria-checked:border-primary aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 flex size-4 items-center justify-center rounded-[4px] border border-gray-300 shadow-xs transition-shadow group-has-disabled/field:opacity-50 focus-visible:ring-[3px] aria-invalid:ring-[3px] peer relative shrink-0 outline-none after:absolute after:-inset-x-3 after:-inset-y-2 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="[&>svg]:size-3.5 grid place-content-center text-current transition-none"
      >
        {props.indeterminate ? <MinusIcon /> : <CheckIcon />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
